import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { X, Upload, Calendar, Download } from 'lucide-react';
import saasApiService from '../../../services/saasApi';

const schema = yup.object().shape({
  trailer_type: yup
    .string()
    .oneOf(['carreta', 'furgon', 'plataforma', 'cama_baja', 'cisterna', 'baranda', 'otro'])
    .required('Tipo requerido'),
  year: yup.number().transform(v => (isNaN(v) ? undefined : v)).min(1900).max(new Date().getFullYear() + 1).optional(),
  capacity_tons: yup
    .number()
    .transform(v => (isNaN(v) ? undefined : v))
    .min(0, 'Capacidad debe ser positiva')
    .optional(),
  status: yup.string().oneOf(['active', 'maintenance', 'inactive']).required(),
  // Fechas de vencimiento
  registration_expiry: yup.string().optional(), // TUCE
  citv_expiry: yup.string().optional(),         // CITV
});

interface TrailerFormProps {
  trailer?: any | null;
  onClose: () => void;
  onSave: () => void;
}

const TrailerForm: React.FC<TrailerFormProps> = ({ trailer, onClose, onSave }) => {
  const [submitError, setSubmitError] = useState<string>('');
  const [registrationFile, setRegistrationFile] = useState<File | null>(null); // TUCE
  const [citvFile, setCitvFile] = useState<File | null>(null);                 // CITV
  const [tiveFile, setTiveFile] = useState<File | null>(null);                 // TIVE

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: yupResolver(schema) as any,
    defaultValues: {
      trailer_type: 'carreta',
      brand: '',
      model: '',
      year: '',
      plate: '',
      capacity_tons: '',
      status: 'active',
      registration_expiry: '',
      citv_expiry: '',
      notes: '',
    },
  });

  useEffect(() => {
    if (trailer) {
      reset({
        trailer_type: trailer.trailer_type || 'carreta',
        brand: trailer.brand || '',
        model: trailer.model || '',
        year: trailer.year || '',
        plate: trailer.plate || '',
        capacity_tons: trailer.capacity_tons ?? '',
        status: trailer.status || 'active',
        registration_expiry: (trailer as any)?.registration_expiry ? String((trailer as any).registration_expiry).split('T')[0] : '',
        citv_expiry: (trailer as any)?.citv_expiry ? String((trailer as any).citv_expiry).split('T')[0] : '',
        notes: trailer.notes || '',
      });
    }
  }, [trailer, reset]);

  // Build absolute URL to backend static files
  const getFileUrl = (relativePath?: string) => {
    if (!relativePath) return '#';
    const apiBase = process.env.REACT_APP_SAAS_API_URL || 'http://localhost:5000/api';
    const backendBase = apiBase.replace(/\/?api\/?$/, '');
    const cleanPath = relativePath.replace(/^\//, '');
    return `${backendBase}/${cleanPath}`;
  };

  const onSubmit = async (data: any) => {
    setSubmitError('');
    try {
      const formData = new FormData();
      Object.keys(data).forEach((key) => {
        const value = (data as any)[key];
        if (value !== '' && value != null) formData.append(key, value);
      });

      if (registrationFile) formData.append('registration_document', registrationFile);
      if (citvFile) formData.append('citv_document', citvFile);
      if (tiveFile) formData.append('tive_document', tiveFile);

      if (trailer) await saasApiService.updateTrailer(trailer.id, formData);
      else await saasApiService.createTrailer(formData);
      onSave();
      onClose();
    } catch (err: any) {
      const msg = err?.response?.data?.message || err?.message || 'Error al guardar el remolque';
      // Adjuntar detalle de validación si existe
      const errors = err?.response?.data?.errors;
      const detail = Array.isArray(errors) ? errors.map((e: any) => e.msg || e.message).join('. ') : '';
      setSubmitError(detail ? `${msg}. ${detail}` : msg);
      console.error('TrailerForm submit error:', err?.response?.data || err);
    }
  };

  const handleFileChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    type: 'registration' | 'citv' | 'tive'
  ) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (type === 'registration') setRegistrationFile(file);
    else if (type === 'citv') setCitvFile(file);
    else if (type === 'tive') setTiveFile(file);
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-2xl shadow-lg rounded-md bg-white">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-lg font-medium text-gray-900">{trailer ? 'Editar Remolque' : 'Agregar Remolque'}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="h-6 w-6" />
          </button>
        </div>

        {submitError && (
          <div className="mb-4 p-3 rounded-md bg-red-50 text-red-700 text-sm border border-red-200">
            {submitError}
          </div>
        )}
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700">Tipo *</label>
              <select {...register('trailer_type')} className="mt-1 block w-full border border-gray-300 rounded-md py-2 px-3">
                <option value="carreta">Carreta</option>
                <option value="furgon">Furgón</option>
                <option value="plataforma">Plataforma</option>
                <option value="cama_baja">Cama Baja</option>
                <option value="cisterna">Cisterna</option>
                <option value="baranda">Baranda</option>
                <option value="otro">Otro</option>
              </select>
              {errors.trailer_type && <p className="text-sm text-red-600 mt-1">{String(errors.trailer_type.message)}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Estado *</label>
              <select {...register('status')} className="mt-1 block w-full border border-gray-300 rounded-md py-2 px-3">
                <option value="active">Activo</option>
                <option value="maintenance">Mantenimiento</option>
                <option value="inactive">Inactivo</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Marca</label>
              <input {...register('brand')} className="mt-1 block w-full border border-gray-300 rounded-md py-2 px-3" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Modelo</label>
              <input {...register('model')} className="mt-1 block w-full border border-gray-300 rounded-md py-2 px-3" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Año</label>
              <input type="number" {...register('year')} className="mt-1 block w-full border border-gray-300 rounded-md py-2 px-3" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Placa</label>
              <input {...register('plate')} className="mt-1 block w-full border border-gray-300 rounded-md py-2 px-3" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Capacidad (Tn)</label>
              <input type="number" step="0.1" {...register('capacity_tons')} className="mt-1 block w-full border border-gray-300 rounded-md py-2 px-3" />
            </div>
          </div>

          {/* Fechas de vencimiento (TUCE, CITV) */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700">
                <Calendar className="inline h-4 w-4 mr-1" />
                Vencimiento TUCE
              </label>
              <input
                type="date"
                {...register('registration_expiry')}
                className="mt-1 block w-full border border-gray-300 rounded-md py-2 px-3 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">
                <Calendar className="inline h-4 w-4 mr-1" />
                Vencimiento CITV
              </label>
              <input
                type="date"
                {...register('citv_expiry')}
                className="mt-1 block w-full border border-gray-300 rounded-md py-2 px-3 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>

          {/* Documentos: TUCE, CITV, TIVE */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700">TUCE (Tarjeta Única de Circulación)</label>
              <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md">
                <div className="space-y-1 text-center">
                  <Upload className="mx-auto h-12 w-12 text-gray-400" />
                  <div className="flex text-sm text-gray-600">
                    <label className="relative cursor-pointer bg-white rounded-md font-medium text-blue-600 hover:text-blue-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-blue-500">
                      <span>Subir archivo</span>
                      <input type="file" className="sr-only" accept=".pdf,.jpg,.jpeg,.png" onChange={(e) => handleFileChange(e, 'registration')} />
                    </label>
                  </div>
                  <p className="text-xs text-gray-500">PDF, PNG, JPG hasta 5MB</p>
                  {registrationFile && <p className="text-sm text-green-600">{registrationFile.name}</p>}
                  {!registrationFile && (trailer as any)?.registration_document && (
                    <a href={getFileUrl((trailer as any).registration_document)} target="_blank" rel="noopener noreferrer" className="inline-flex items-center text-blue-600 hover:text-blue-800 text-sm">
                      <Download className="h-4 w-4 mr-1" /> Descargar TUCE
                    </a>
                  )}
                </div>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">CITV</label>
              <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md">
                <div className="space-y-1 text-center">
                  <Upload className="mx-auto h-12 w-12 text-gray-400" />
                  <div className="flex text-sm text-gray-600">
                    <label className="relative cursor-pointer bg-white rounded-md font-medium text-blue-600 hover:text-blue-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-blue-500">
                      <span>Subir archivo</span>
                      <input type="file" className="sr-only" accept=".pdf,.jpg,.jpeg,.png" onChange={(e) => handleFileChange(e, 'citv')} />
                    </label>
                  </div>
                  <p className="text-xs text-gray-500">PDF, PNG, JPG hasta 5MB</p>
                  {citvFile && <p className="text-sm text-green-600">{citvFile.name}</p>}
                  {!citvFile && (trailer as any)?.citv_document && (
                    <a href={getFileUrl((trailer as any).citv_document)} target="_blank" rel="noopener noreferrer" className="inline-flex items-center text-blue-600 hover:text-blue-800 text-sm">
                      <Download className="h-4 w-4 mr-1" /> Descargar CITV
                    </a>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700">TIVE (sin fecha)</label>
              <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md">
                <div className="space-y-1 text-center">
                  <Upload className="mx-auto h-12 w-12 text-gray-400" />
                  <div className="flex text-sm text-gray-600">
                    <label className="relative cursor-pointer bg-white rounded-md font-medium text-blue-600 hover:text-blue-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-blue-500">
                      <span>Subir archivo</span>
                      <input type="file" className="sr-only" accept=".pdf,.jpg,.jpeg,.png" onChange={(e) => handleFileChange(e, 'tive')} />
                    </label>
                  </div>
                  <p className="text-xs text-gray-500">PDF, PNG, JPG hasta 5MB</p>
                  {tiveFile && <p className="text-sm text-green-600">{tiveFile.name}</p>}
                  {!tiveFile && (trailer as any)?.tive_document && (
                    <a href={getFileUrl((trailer as any).tive_document)} target="_blank" rel="noopener noreferrer" className="inline-flex items-center text-blue-600 hover:text-blue-800 text-sm">
                      <Download className="h-4 w-4 mr-1" /> Descargar TIVE
                    </a>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Notas</label>
            <textarea {...register('notes')} rows={3} className="mt-1 block w-full border border-gray-300 rounded-md py-2 px-3" />
          </div>

          <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
            <button type="button" onClick={onClose} className="px-4 py-2 border rounded-md bg-white">Cancelar</button>
            <button type="submit" disabled={isSubmitting} className="px-4 py-2 rounded-md text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50">
              {isSubmitting ? 'Guardando...' : (trailer ? 'Actualizar' : 'Crear')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default TrailerForm;
